import React from "react";

import { canMoveFieldByDirection } from "@common/utils/field-reorder.utils";

import { FieldQueryPredefinedControlType, FieldQueryProps, FieldQueryItem } from "./field-query.types";
import { getProcessedControls } from "./controls";

export const useFieldQueryController = <T,>(props: FieldQueryProps<T>) => {
  const { items, controls } = props;

  const getControl = React.useCallback(
    (type: FieldQueryPredefinedControlType) => {
      return controls.find((control) => control.type === type);
    },
    [controls]
  );

  const [selectedItemId, setSelectedItemId] = React.useState<FieldQueryItem["id"] | undefined>();

  const handleItemSelect = React.useCallback((item: FieldQueryItem) => {
    setSelectedItemId(item.id);
  }, []);

  const handleItemMove = React.useCallback(
    (direction: "up" | "down") => {
      if (selectedItemId) {
        const currentIndex = items.findIndex((f) => f.id === selectedItemId)!;
  
        if (currentIndex < 0 || currentIndex >= items.length) {
          return;
        }
  
        const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
  
        if (newIndex < 0 || newIndex >= items.length) {
          return;
        }
  
        const movedItem = items[currentIndex];
        const replacedItem = items[newIndex];
  
        if (!movedItem || !replacedItem) {
          return;
        }

        if (
          !canMoveFieldByDirection(
            items as Array<{ parentField?: unknown | null }>,
            currentIndex,
            direction
          )
        ) {
          return;
        }

        const updatedItems = items.map((item, index): FieldQueryItem => {
          if (index === currentIndex && replacedItem) {
            return replacedItem;
          }
  
          if (index === newIndex && movedItem) {
            return movedItem;
          }
  
          return item;
        });
  
        const control = getControl(
          direction === "down" ? FieldQueryPredefinedControlType.moveDown : FieldQueryPredefinedControlType.moveUp
        );
  
        if (control?.handler) {
          control.handler(updatedItems);
        }
      }
    },
    [getControl, items, selectedItemId]
  );

  const handleAllRemoveClick = React.useCallback(() => {
    const control = getControl(FieldQueryPredefinedControlType.removeAll);

    if (control?.handler) {
      control.handler([]);
    }
  }, [getControl]);

  const processedControls = getProcessedControls({
    controls,
    items,
    selectedItemId,
    onAllItemsRemove: handleAllRemoveClick,
    onItemMove: handleItemMove,
  });

  return {
    selectedItemId,
    handleItemSelect,
    processedControls,
  };
};
